import { useState } from "react";
import { captureException } from "@sentry/nextjs";
import { assertPresence } from "utils/assertions";
import SignedPutUrlFileUpload, {
  useSignedPutUrlFileUpload,
} from "./SignedPutUrlFileUpload";
import {
  DocumentFileUploadError,
  DocumentFileUploadWrappedError,
} from "./errors";

export type DocumentData = {
  file: string;
  fileSize: number;
  id: UUID;
  mimeType: string;
  originalFilename: string;
};

type PerformCreate<R> = (documentData: DocumentData) => Promise<R>;

export default class DocumentFileUpload<R> extends EventTarget {
  public fileUpload: SignedPutUrlFileUpload;

  protected performCreate: PerformCreate<R>;

  public createdDocument: R | undefined;

  public isCreatingDocument = false;

  constructor(
    fileUpload: SignedPutUrlFileUpload,
    performCreate: PerformCreate<R>,
  ) {
    super();
    this.fileUpload = fileUpload;
    this.performCreate = performCreate;
  }

  async startDocumentUpload() {
    await this.fileUpload.startUploadToPath();
    return this.createDocument();
  }

  async createDocument() {
    try {
      const { key } = this.fileUpload.signedUrlData || {};

      assertPresence(key, "Could not get file key!");

      this.isCreatingDocument = true;
      this.dispatchEvent(new Event("createDocument:start"));

      const { name, size, type } = this.fileUpload.file;

      const documentData: DocumentData = {
        file: key,
        fileSize: size,
        id: this.fileUpload.id,
        mimeType: type,
        originalFilename: name,
      };

      this.createdDocument = await this.performCreate(documentData);

      this.dispatchEvent(new Event("createDocument:complete"));
      this.isCreatingDocument = false;

      return this.createdDocument;
    } catch (e) {
      if (e instanceof Error) {
        throw this.fileUploadError(e);
      } else {
        throw e;
      }
    }
  }

  retryDocumentUpload({
    forceNewSignedUrl,
    forceNewUpload,
  }: { forceNewSignedUrl?: boolean; forceNewUpload?: boolean } = {}) {
    if (this.fileUpload.isSuccess && !forceNewUpload)
      return this.createDocument();
    return this.fileUpload.retryUploadToPath(forceNewSignedUrl);
  }

  protected fileUploadError(message: string): DocumentFileUploadError<R>;
  protected fileUploadError<TError extends Error>(
    error: TError,
  ): DocumentFileUploadWrappedError<TError, R>;
  protected fileUploadError(messageOrError: Error | string) {
    const error =
      typeof messageOrError === "string"
        ? new DocumentFileUploadError(messageOrError, this)
        : new DocumentFileUploadWrappedError(messageOrError, this);

    captureException(error, { extra: this.getSentryExtra() });
    this.dispatchEvent(new ErrorEvent("error", { error }));
    return error;
  }

  getSentryExtra() {
    return { fileUpload: this.fileUpload.getSentryExtra() };
  }
}

export function useDocumentFileUpload(upload: DocumentFileUpload<unknown>) {
  const [isCreatingDocument, setIsCreatingDocument] = useState(
    upload.isCreatingDocument,
  );
  const [isDocumentCreated, setIsDocumentCreated] = useState(false);

  upload.addEventListener("createDocument:start", () =>
    setIsCreatingDocument(true),
  );

  upload.addEventListener("createDocument:complete", () => {
    setIsCreatingDocument(false);
    setIsDocumentCreated(true);
  });

  upload.addEventListener("error", () => {
    setIsCreatingDocument(false);
  });

  const { loading, progress, isDone, error } = useSignedPutUrlFileUpload(
    upload.fileUpload,
  );

  return {
    loading: !isDocumentCreated && (loading || isCreatingDocument),
    isDone: isDone && isDocumentCreated,
    progress,
    error,
  };
}
